import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PhysicalProduct,
  CreateProductRequest,
  UpdateProductRequest,
} from "types/physical-product";

const QUERY_KEY = "admin-physical-products";

export const usePhysicalProducts = () => {
  const { client, session } = useAuthContext();
  const queryClient = useQueryClient();

  const getProducts = async (): Promise<PhysicalProduct[]> => {
    if (!client) throw new Error("Client not initialized");
    if (!session) throw new Error("No active session");

    const { data, error } = await client.functions.invoke("admin-list-products", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;
    return data.products || [];
  };

  const {
    data: products,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: getProducts,
    staleTime: 1000 * 60 * 2,
    enabled: !!session,
  });

  const createMutation = useMutation({
    mutationFn: async (product: CreateProductRequest): Promise<PhysicalProduct> => {
      if (!client) throw new Error("Client not initialized");
      if (!session) throw new Error("No active session");

      const { data, error } = await client.functions.invoke("admin-create-product", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: product,
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      return data.product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (product: UpdateProductRequest): Promise<PhysicalProduct> => {
      if (!client) throw new Error("Client not initialized");
      if (!session) throw new Error("No active session");

      const { data, error } = await client.functions.invoke("admin-update-product", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: product,
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      return data.product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!client) throw new Error("Client not initialized");
      if (!session) throw new Error("No active session");

      const { data, error } = await client.functions.invoke("admin-delete-product", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { id },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  return {
    products,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
    createProduct: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    updateProduct: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    deleteProduct: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  };
};
